import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = { class: "flex items-center" }

import { computed, toRefs, ref } from 'vue';
import useTokens from '@/composables/useTokens';
import { FullPool } from '@/services/balancer/subgraph/types';
import { TokenInfoMap } from '@/types/TokenList';
import { bnum } from '@/lib/utils';
import useNumbers from '@/composables/useNumbers';
import InvestSummary from './components/InvestSummary.vue';
import TokenAmounts from './components/TokenAmounts.vue';
import InvestActions from './components/InvestActions.vue';
import { InvestMathResponse } from '../../composables/useInvestMath';
import { useI18n } from 'vue-i18n';
import useInvestState from '../../composables/useInvestState';

/**
 * TYPES
 */
type Props = {
  pool: FullPool;
  math: InvestMathResponse;
  tokenAddresses: string[];
};

type AmountMap = {
  [address: string]: string;
};

/**
 * PROPS & EMITS
 */

export default /*#__PURE__*/_defineComponent({
  __name: 'InvestPreviewModal',
  props: {
    pool: null,
    math: null,
    tokenAddresses: null
  },
  emits: ["close"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'close'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { pool: FullPool, math: InvestMathResponse, tokenAddresses: string[] };





/**
 * STATE
 */
const investmentConfirmed = ref(false);

/**
 * COMPOSABLES
 */
const { t } = useI18n();
const { getToken } = useTokens();
const { toFiat } = useNumbers();
const { fullAmounts, priceImpact, highPriceImpact } = toRefs(props.math);
const { resetAmounts } = useInvestState();

/**
 * COMPUTED
 */
const title = computed((): string =>
  investmentConfirmed.value
    ? t('investment.preview.titles.confirmed')
    : t('investment.preview.titles.default')
);

const amountMap = computed((): AmountMap => {
  const amountMap = {};
  fullAmounts.value.forEach((amount, i) => {
    if (hasAmount(i)) amountMap[props.tokenAddresses[i]] = amount;
  });
  return amountMap;
});

const tokenMap = computed((): TokenInfoMap => {
  const tokenMap = {};
  Object.keys(amountMap.value).forEach((address) => {
    tokenMap[address] = getToken(address);
  });
  return tokenMap;
});

const fiatAmountMap = computed((): AmountMap => {
  const fiatAmountMap = {};
  Object.keys(amountMap.value).forEach((address) => {
    fiatAmountMap[address] = toFiat(amountMap.value[address], address);
  });
  return fiatAmountMap;
});

const fiatTotal = computed((): string =>
  Object.values(fiatAmountMap.value).reduce(
    (total, amount) => bnum(total).plus(amount).toString(),
    '0'
  )
);

/**
 * METHODS
 */
function hasAmount(index: number): boolean {
  return bnum(fullAmounts.value[index]).gt(0);
}

function handleClose(): void {
  if (investmentConfirmed.value) {
    resetAmounts();
  }
  emit('close');
}

return (_ctx: any,_cache: any) => {
  const _component_BalIcon = _resolveComponent("BalIcon")!
  const _component_BalCircle = _resolveComponent("BalCircle")!
  const _component_BalModal = _resolveComponent("BalModal")!

  return (_openBlock(), _createBlock(_component_BalModal, {
    show: "",
    fireworks: investmentConfirmed.value,
    onClose: handleClose
  }, {
    header: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        (investmentConfirmed.value)
          ? (_openBlock(), _createBlock(_component_BalCircle, {
              key: 0,
              size: "8",
              color: "emerald",
              class: "text-white mr-2"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_BalIcon, { name: "akar-icons:check" })
              ]),
              _: 1
            }))
          : _createCommentVNode("", true),
        _createElementVNode("h4", null, _toDisplayString(_unref(title)), 1)
      ])
    ]),
    default: _withCtx(() => [
      _createVNode(TokenAmounts, {
        amountMap: _unref(amountMap),
        tokenMap: _unref(tokenMap),
        fiatAmountMap: _unref(fiatAmountMap),
        fiatTotal: _unref(fiatTotal)
      }, null, 8, ["amountMap", "tokenMap", "fiatAmountMap", "fiatTotal"]),
      _createVNode(InvestSummary, {
        pool: __props.pool,
        fiatTotal: _unref(fiatTotal),
        priceImpact: _unref(priceImpact),
        highPriceImpact: _unref(highPriceImpact)
      }, null, 8, ["pool", "fiatTotal", "priceImpact", "highPriceImpact"]),
      _createVNode(InvestActions, {
        pool: __props.pool,
        math: __props.math,
        tokenAddresses: __props.tokenAddresses,
        class: "mt-4",
        onSuccess: _cache[0] || (_cache[0] = ($event: any) => (investmentConfirmed.value = true))
      }, null, 8, ["pool", "math", "tokenAddresses"])
    ]),
    _: 1
  }, 8, ["fireworks"]))
}
}

})